/**
 * DealerConsole — Super-admin dealer management page.
 *
 * D2 spec:
 * - Table: ☐ multi-select · dealer name · city/state · status pill · seats used/total
 *   (open seats highlighted) · last login · Account Status toggle · 👁 quick-view
 * - Filters: All / Active / Inactive / Open Seats
 * - Type-ahead, sort, Export (filtered view, or selected rows via bulk bar)
 * - Quick-view drawer (without navigating away)
 * - Status toggle: Active ↔ Inactive (reversible)
 *
 * Routes: /super-admin/dealers
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../ui/PageHeader';
import { Button } from '../ui/Button';
import StatusPill, { humanizeEnum } from '../ui/StatusPill';
import { Input } from '../ui/Input';
import Slideover from '../ui/Slideover';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../ui/Table';
import useTableSort from '../ui/useTableSort';
import { compareText } from '../../utils/tableSort';
import { SkeletonTable } from '../ui/Skeleton';
import { superAdminAPI } from '../../services/api';
import { EyeIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { dealerToday } from '../../utils/dealerDate';

// IDI-479: click-to-sort columns (replaces the bespoke String.localeCompare
// sort, which ordered Seats as text and Last Login as a string).
const DEALER_SORT_COLUMNS = [
  { key: 'name', type: 'text', value: (d) => d.name || null },
  { key: 'cityState', type: 'text', value: (d) => [d.city, d.state].filter(Boolean).join(', ') || null },
  { key: 'status', type: 'text', value: (d) => humanizeEnum(d.status) || null },
  { key: 'seatsUsed', type: 'number', value: (d) => d.seatsUsed },
  { key: 'lastLogin', type: 'date', value: (d) => d.lastLogin || null },
  { key: 'account', type: 'text', value: (d) => (d.status === 'active' ? 'Active' : 'Inactive') },
];

// Filter tabs
const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'open_seats', label: 'Open Seats' },
];

export default function DealerConsole() {
  const navigate = useNavigate();
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [quickViewDealer, setQuickViewDealer] = useState(null);

  // Load dealers
  useEffect(() => {
    async function loadDealers() {
      try {
        setLoadError(false);
        const res = await superAdminAPI.getDealers();
        const arr = Array.isArray(res) ? res : (res?.data || res?.items || []);
        setDealers(arr.map((d) => ({
          id: d.id || d.dealerId,
          name: d.name || d.companyName || 'Unnamed Contractor',
          city: d.city || '',
          state: d.state || '',
          status: d.status || (d.active === false ? 'inactive' : 'active'),
          seatsUsed: d.seatsUsed ?? d.seats_used ?? 0,
          seatsTotal: d.seatsTotal ?? d.seats_total ?? 0,
          lastLogin: d.lastLogin || d.last_login || null,
        })));
      } catch {
        // IDI-65: surface the failure instead of rendering an empty table that
        // looks like "no contractors". Never fabricate content from a failed load.
        setLoadError(true);
        setDealers([]);
      } finally {
        setLoading(false);
      }
    }
    loadDealers();
  }, []);

  // Filter + search
  const filteredDealers = useMemo(() => {
    let result = [...dealers];

    // Apply filter
    if (filter === 'active') result = result.filter((d) => d.status === 'active');
    else if (filter === 'inactive') result = result.filter((d) => d.status === 'inactive');
    else if (filter === 'open_seats') result = result.filter((d) => (d.seatsTotal - d.seatsUsed) > 0);

    // Apply search
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((d) =>
        d.name?.toLowerCase().includes(q) ||
        d.city?.toLowerCase().includes(q) ||
        d.state?.toLowerCase().includes(q)
      );
    }

    // Default order (the header-sort "off" state): name ascending.
    result.sort((a, b) => compareText(a.name || '', b.name || ''));

    return result;
  }, [dealers, filter, search]);

  const { sortedRows: sortedDealers, getSortProps } = useTableSort(filteredDealers, DEALER_SORT_COLUMNS);

  // Selection
  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    if (selected.length === filteredDealers.length) setSelected([]);
    else setSelected(filteredDealers.map((d) => d.id));
  };

  // Status toggle
  const handleStatusToggle = useCallback(async (dealer) => {
    const newStatus = dealer.status === 'active' ? 'inactive' : 'active';
    // Optimistic update, then persist.
    setDealers((prev) => prev.map((d) => d.id === dealer.id ? { ...d, status: newStatus } : d));
    try {
      await superAdminAPI.updateDealer(dealer.id, { status: newStatus });
    } catch {
      setDealers((prev) => prev.map((d) => d.id === dealer.id ? { ...d, status: dealer.status } : d));
    }
  }, []);

  // Export to CSV
  const handleExport = useCallback(() => {
    const dataToExport = selected.length > 0
      ? filteredDealers.filter((d) => selected.includes(d.id))
      : filteredDealers;
    const cols = ['name', 'city', 'state', 'status', 'seatsUsed', 'seatsTotal', 'lastLogin'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [cols.join(',')].concat(
      dataToExport.map((d) => cols.map((c) => esc(d[c])).join(','))
    );
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dealers-${dealerToday()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredDealers, selected]);

  // IDI-273: table renders 8 columns (colSpan={8} at :254) — was hardcoded to 7.
  if (loading) return <div className="px-6"><SkeletonTable rows={8} cols={8} /></div>;

  if (loadError) {
    return (
      <div className="px-6">
        <PageHeader title="Contractor Console" />
        <div
          className="flex flex-col items-center justify-center text-center py-16 px-6 rounded-lg border"
          style={{ borderColor: 'var(--color-border)', background: 'var(--color-surface)' }}
        >
          <ExclamationTriangleIcon
            className="h-10 w-10 mb-3"
            style={{ color: 'var(--color-text-secondary)' }}
            aria-hidden="true"
          />
          <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
            Couldn't load contractors
          </p>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            The request failed. This is not an empty list — try again.
          </p>
          <div className="mt-4">
            <Button variant="secondary" onClick={() => window.location.reload()}>Try again</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 space-y-4">
      <PageHeader
        title="Contractor Console"
        subtitle={`${filteredDealers.length} contractor${filteredDealers.length !== 1 ? 's' : ''}`}
        actions={<Button variant="secondary" onClick={handleExport}>Export</Button>}
      />

      {/* Filters + Search */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className="px-3 py-1.5 text-sm rounded-full border transition-colors"
              style={{
                borderColor: filter === f.id ? 'var(--red-400)' : 'var(--color-border)',
                backgroundColor: filter === f.id ? 'var(--red-50)' : 'var(--color-surface)',
                color: filter === f.id ? 'var(--red-700)' : 'var(--color-text-primary)',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="w-64">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contractors…"
            size="sm"
          />
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.length > 0 && (
        <div
          className="flex items-center gap-3 px-4 py-2" style={{ borderRadius: 'var(--radius-md)', backgroundColor: 'var(--neutral-100)' }}
        >
          <span className="text-sm font-medium">{selected.length} selected</span>
          <Button size="sm" variant="secondary" onClick={handleExport}>Export Selected</Button>
          <Button size="sm" variant="secondary" onClick={() => setSelected([])}>Clear</Button>
        </div>
      )}

      {/* Table */}
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader className="w-8">
              <input
                type="checkbox"
                checked={selected.length === filteredDealers.length && filteredDealers.length > 0}
                onChange={toggleSelectAll}
                className="rounded border-[color:var(--color-border)] text-red-600 focus:ring-red-500"
                style={{ accentColor: 'var(--color-action-primary)' }}
              />
            </TableHeader>
            <TableHeader {...getSortProps('name')}>Contractor Name</TableHeader>
            <TableHeader {...getSortProps('cityState')}>City/State</TableHeader>
            <TableHeader {...getSortProps('status')}>Status</TableHeader>
            <TableHeader {...getSortProps('seatsUsed')}>Seats</TableHeader>
            <TableHeader {...getSortProps('lastLogin')}>Last Login</TableHeader>
            <TableHeader {...getSortProps('account')}>Account</TableHeader>
            <TableHeader className="w-10"></TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredDealers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-8">
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  {dealers.length === 0 ? 'No contractors configured.' : 'No contractors match your filters.'}
                </p>
              </TableCell>
            </TableRow>
          ) : (
            sortedDealers.map((dealer) => {
              const openSeats = (dealer.seatsTotal || 0) - (dealer.seatsUsed || 0);
              return (
                <TableRow key={dealer.id}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selected.includes(dealer.id)}
                      onChange={() => toggleSelect(dealer.id)}
                      className="rounded border-[color:var(--color-border)] text-red-600 focus:ring-red-500"
                      style={{ accentColor: 'var(--color-action-primary)' }}
                    />
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      className="font-medium text-sm hover:underline text-left"
                      style={{ color: 'var(--color-action-primary)' }}
                      onClick={() => navigate(`/super-admin/dealers/${dealer.id}`)}
                    >
                      {dealer.name}
                    </button>
                  </TableCell>
                  <TableCell>{dealer.city}, {dealer.state}</TableCell>
                  <TableCell><StatusPill status={dealer.status} /></TableCell>
                  <TableCell>
                    <span className={openSeats > 0 ? 'font-medium' : ''} style={{ color: openSeats > 0 ? 'var(--color-action-primary)' : undefined }}>
                      {dealer.seatsUsed}/{dealer.seatsTotal}
                    </span>
                    {openSeats > 0 && (
                      <span className="ml-1 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                        ({openSeats} open)
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    {dealer.lastLogin ? new Date(dealer.lastLogin).toLocaleDateString() : 'Never'}
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => handleStatusToggle(dealer)}
                      className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
                      style={{ backgroundColor: dealer.status === 'active' ? 'var(--color-action-primary)' : 'var(--neutral-300)' }}
                      aria-label={`Toggle ${dealer.name} ${dealer.status === 'active' ? 'inactive' : 'active'}`}
                    >
                      <span
                        className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform"
                        style={{ transform: dealer.status === 'active' ? 'translateX(18px)' : 'translateX(3px)' }}
                      />
                    </button>
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => setQuickViewDealer(dealer)}
                      className="text-sm"
                      style={{ color: 'var(--color-text-secondary)' }}
                      title="Quick view"
                    >
                      <EyeIcon className="h-4 w-4" aria-label="Quick view" />
                    </button>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Quick-view drawer */}
      <Slideover
        open={!!quickViewDealer}
        onClose={() => setQuickViewDealer(null)}
        title={quickViewDealer?.name || 'Contractor'}
        subtitle="Quick View"
        width="md"
      >
        {quickViewDealer && (
          <div className="px-6 space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Status</p>
                <StatusPill status={quickViewDealer.status} />
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Location</p>
                <p>{quickViewDealer.city}, {quickViewDealer.state}</p>
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Seats</p>
                <p>{quickViewDealer.seatsUsed}/{quickViewDealer.seatsTotal} used</p>
              </div>
              <div>
                <p className="text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Last Login</p>
                <p>{quickViewDealer.lastLogin ? new Date(quickViewDealer.lastLogin).toLocaleDateString() : 'Never'}</p>
              </div>
            </div>
            <Button onClick={() => { setQuickViewDealer(null); navigate(`/super-admin/dealers/${quickViewDealer.id}`); }}>
              View Full Details
            </Button>
          </div>
        )}
      </Slideover>
    </div>
  );
}
